// src/pages/LoginPage.tsx — responsive split-screen government portal login
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import axios from 'axios';
import { login } from '../services/api';
import type { ApiErrorBody } from '../types';
import { css } from '../utils/css';

export default function LoginPage() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 820);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(email, password);
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      if (axios.isAxiosError<ApiErrorBody>(err) && err.response) setError(err.response.data?.error || 'Invalid email or password');
      else setError('Cannot reach server. Is the backend running on port 3000?');
    } finally {
      setLoading(false);
    }
  };

  const m = isMobile;

  return (
    <div style={{ ...S.page, flexDirection: m ? 'column' : 'row' }}>

      {/* ════════ Brand panel (LEFT on desktop / TOP on mobile) ════════ */}
      <div style={{ ...S.left, ...(m ? S.leftMobile : {}) }}>
        <div style={S.orbA} />
        {!m && <div style={S.orbB} />}
        <div style={S.dots} />

        {/* Center content */}
        <div style={{ ...S.leftBody, ...(m ? S.leftBodyMobile : {}) }}>
          <div style={{ ...S.logoRow, ...(m ? S.logoRowMobile : {}) }}>
            <div style={S.logoIcon}>U</div>
            <div style={{ textAlign: m ? 'center' : 'left' }}>
              <div style={{ ...S.logoName, fontSize: m ? 26 : 28 }}>Fertilizer Tracking</div>
              <div style={S.logoSub}>Smart Fertilizer Distribution</div>
            </div>
          </div>

          <h1 style={{ ...S.hero, ...(m ? S.heroMobile : {}) }}>
            Track every bag.{m ? ' ' : <br/>}Reach every farmer.
          </h1>

          {/* Desktop-only extras (kept off mobile to stay clean) */}
          {!m && (
            <>
              <p style={S.heroSub}>
                QR-based end-to-end urea bag tracking — from the factory gate
                to the farmer's hands. Real-time dashboard. Zero fraud tolerance.
              </p>
              <div style={S.feats}>
                {[
                  'Factory-to-farmer QR traceability',
                  'Real-time fraud detection & alerts',
                  'Live government dashboard',
                  'Zero app download — works in any browser',
                ].map(f => (
                  <div key={f} style={S.featRow}>
                    <span style={S.featTick}>✓</span>
                    <span style={S.featText}>{f}</span>
                  </div>
                ))}
              </div>
              <div style={S.liveBadge}>
                <span style={S.liveDot} /> SYSTEM LIVE · Real-time tracking active
              </div>
            </>
          )}
        </div>

        {!m && <div style={S.leftFoot}>Ministry of Agriculture · Secure Government Portal</div>}
      </div>

      {/* ════════ Auth panel (RIGHT on desktop / BOTTOM on mobile) ════════ */}
      <div style={{ ...S.right, ...(m ? S.rightMobile : {}) }}>
        <div style={S.authWrap}>

          <div style={S.card}>
            <div style={S.cardAccent} />

            <div style={S.cardTop}>
              <div style={S.brandBadge}>U</div>
              <div>
                <div style={S.cardTitle}>Admin Portal</div>
                <div style={S.cardSub}>Ministry of Agriculture</div>
              </div>
            </div>

            <div style={S.welcomeT}>Welcome back 👋</div>
            <div style={S.welcomeS}>Sign in to access your dashboard</div>

            {error && (
              <div style={S.errBox}><span>⚠️</span><span>{error}</span></div>
            )}

            <form onSubmit={handleSubmit} style={{ marginTop:20 }}>
              <div style={S.field}>
                <label style={S.label}>Email Address</label>
                <div style={S.inputWrap}>
                  <span style={S.inputIcon}>✉️</span>
                  <input
                    style={S.input}
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@ureatrack.gov.in"
                    autoFocus={!m} required
                    onFocus={e => Object.assign(e.target.style, S.inputFocus)}
                    onBlur={e => Object.assign(e.target.style, S.inputBlur)}
                  />
                </div>
              </div>

              <div style={{ ...S.field, marginBottom:8 }}>
                <label style={S.label}>Password</label>
                <div style={S.inputWrap}>
                  <span style={S.inputIcon}>🔒</span>
                  <input
                    style={{ ...S.input, paddingRight:42 }}
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    onFocus={e => Object.assign(e.target.style, S.inputFocus)}
                    onBlur={e => Object.assign(e.target.style, S.inputBlur)}
                  />
                  <span style={S.eye} onClick={() => setShowPass(s => !s)}>{showPass ? '🙈' : '👁️'}</span>
                </div>
              </div>

              <button
                type="submit"
                style={{ ...S.btn, ...((loading || !email || !password) ? S.btnOff : {}) }}
                disabled={loading || !email || !password}
              >
                {loading
                  ? <><span className="spin" style={{ display:'inline-block', marginRight:6 }}>⟳</span> Signing in…</>
                  : 'Login to Dashboard  →'}
              </button>
            </form>

            <div style={S.ssl}><span style={S.sslDot} /> 256-bit SSL Encrypted · Govt-grade security</div>
          </div>

          {/* Demo credentials */}
          <div style={S.demo}>
            <div style={S.demoTitle}>Demo Credentials</div>
            {[
              { role:'Admin',   email:'admin@ureatrack.gov.in',   pass:'Admin@123' },
              { role:'Officer', email:'officer@ureatrack.gov.in', pass:'Admin@123' },
            ].map(d => (
              <div
                key={d.role}
                style={S.demoItem}
                onClick={() => { setEmail(d.email); setPassword(d.pass); }}
                onMouseEnter={e => { e.currentTarget.style.background='#EAF7F1'; e.currentTarget.style.borderColor='#1A9E72'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#F8FDFB'; e.currentTarget.style.borderColor='#E6F1EB'; }}
              >
                <span style={S.demoRole}>{d.role}</span>
                <span style={S.demoEmail}>{d.email}</span>
                <span style={S.demoPass}>{d.pass}</span>
              </div>
            ))}
            <div style={S.demoHint}>Click a row to auto-fill</div>
          </div>

        </div>
      </div>
    </div>
  );
}

const GREEN_BG = `
  radial-gradient(circle at 18% 22%, rgba(45,212,160,0.20), transparent 40%),
  radial-gradient(circle at 90% 80%, rgba(26,158,114,0.18), transparent 45%),
  linear-gradient(150deg, #06281B 0%, #0B5E45 60%, #093724 100%)`;

const S = css({
  page: { display:'flex', minHeight:'100vh', background:'#0B5E45', fontFamily:"'Segoe UI',-apple-system,Roboto,sans-serif" },

  // ── Brand panel ──
  left: { flex:'1.15 1 0', position:'relative', overflow:'hidden', background:GREEN_BG, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'40px 56px' },
  leftMobile: { flex:'none', width:'100%', justifyContent:'flex-start', alignItems:'center', textAlign:'center', padding:'40px 22px 30px' },

  orbA: { position:'absolute', top:'-130px', left:'-110px', width:'360px', height:'360px', borderRadius:'50%', background:'radial-gradient(circle,rgba(45,212,160,0.30),transparent 70%)', filter:'blur(30px)', pointerEvents:'none' },
  orbB: { position:'absolute', bottom:'-150px', right:'-80px', width:'400px', height:'400px', borderRadius:'50%', background:'radial-gradient(circle,rgba(26,158,114,0.26),transparent 70%)', filter:'blur(40px)', pointerEvents:'none' },
  dots: { position:'absolute', inset:0, opacity:0.45, pointerEvents:'none', backgroundImage:'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize:'24px 24px' },

  gov: { position:'relative', zIndex:1, display:'flex', alignItems:'center', gap:9, fontSize:13, fontWeight:800, color:'rgba(255,255,255,0.9)', letterSpacing:'0.16em', textTransform:'uppercase' },
  govMobile: { justifyContent:'center', fontSize:11, letterSpacing:'0.12em', flexWrap:'wrap' },
  govHindi: { fontWeight:700, letterSpacing:'0.03em' },
  govDot: { color:'rgba(255,255,255,0.4)' },

  leftBody: { position:'relative', zIndex:1, maxWidth:480, alignSelf:'flex-start', display:'flex', flexDirection:'column', justifyContent:'center', flex:1, paddingTop:24, paddingBottom:24 },
  leftBodyMobile: { alignSelf:'center', alignItems:'center', flex:'none', maxWidth:420, paddingTop:26, paddingBottom:0 },

  logoRow: { display:'flex', alignItems:'center', gap:15, marginBottom:30 },
  logoRowMobile: { justifyContent:'center', gap:13, marginBottom:18 },
  logoIcon: { width:54, height:54, borderRadius:16, background:'linear-gradient(135deg,#2DD4A0,#1A9E72)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:800, color:'#06281B', flexShrink:0, boxShadow:'0 10px 30px rgba(45,212,160,0.4)' },
  logoName: { fontWeight:800, color:'#fff', lineHeight:1, letterSpacing:'-0.01em' },
  logoSub: { fontSize:12, color:'rgba(255,255,255,0.55)', marginTop:5 },

  hero: { fontSize:38, fontWeight:800, color:'#fff', lineHeight:1.16, letterSpacing:'-0.025em', marginBottom:18 },
  heroMobile: { fontSize:24, marginBottom:0, maxWidth:340 },

  heroSub: { fontSize:14, color:'rgba(255,255,255,0.66)', lineHeight:1.75, marginBottom:32, maxWidth:430 },
  feats: { display:'flex', flexDirection:'column', gap:14 },
  featRow: { display:'flex', alignItems:'center', gap:13 },
  featTick: { width:26, height:26, borderRadius:'50%', background:'rgba(45,212,160,0.16)', border:'1px solid rgba(45,212,160,0.45)', color:'#2DD4A0', fontSize:13, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  featText: { fontSize:14, color:'rgba(255,255,255,0.85)', fontWeight:500 },
  liveBadge: { display:'inline-flex', alignItems:'center', gap:8, marginTop:24, padding:'7px 16px', background:'rgba(45,212,160,0.12)', border:'1px solid rgba(45,212,160,0.4)', borderRadius:20, fontSize:11.5, fontWeight:700, color:'#2DD4A0', letterSpacing:'0.04em', alignSelf:'flex-start' },
  liveDot: { width:8, height:8, borderRadius:'50%', background:'#2DD4A0', animation:'livePulse 1.8s infinite' },
  leftFoot: { position:'relative', zIndex:1, fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:'0.03em' },

  // ── Auth panel ──
  right: { flex:'0.85 1 0', minWidth:0, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px', background:'#F1F8F5' },
  rightMobile: { flex:'none', width:'100%', padding:'26px 18px 36px' },
  authWrap: { width:'100%', maxWidth:384, display:'flex', flexDirection:'column' },

  card: { position:'relative', background:'#fff', borderRadius:22, padding:'28px 26px 24px', boxShadow:'0 24px 60px rgba(6,40,27,0.16)', border:'1px solid #E7F0EB', overflow:'hidden', marginBottom:16 },
  cardAccent: { position:'absolute', top:0, left:0, right:0, height:5, background:'linear-gradient(90deg,#06281B,#1A9E72,#2DD4A0)' },
  cardTop: { display:'flex', alignItems:'center', gap:13, marginBottom:22 },
  brandBadge: { width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#0B5E45,#1A9E72)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:800, color:'#fff', flexShrink:0, boxShadow:'0 8px 18px rgba(26,158,114,0.32)' },
  cardTitle: { fontSize:17, fontWeight:800, color:'#0E1A14', letterSpacing:'-0.01em' },
  cardSub: { fontSize:11.5, color:'#7A8A7E', marginTop:2 },
  welcomeT: { fontSize:25, fontWeight:800, color:'#0E1A14', letterSpacing:'-0.02em', lineHeight:1.1 },
  welcomeS: { fontSize:13, color:'#7A8A7E', marginTop:5 },

  errBox: { display:'flex', alignItems:'center', gap:8, background:'#FDECEC', border:'1px solid #F3C9C9', color:'#B02A2A', borderRadius:10, padding:'10px 13px', fontSize:12, marginTop:16, fontWeight:500 },

  field: { marginBottom:16 },
  label: { display:'block', fontSize:12, fontWeight:700, color:'#2E3D34', marginBottom:7 },
  inputWrap: { position:'relative', display:'flex', alignItems:'center' },
  inputIcon: { position:'absolute', left:14, fontSize:14, opacity:0.6, pointerEvents:'none' },
  input: { width:'100%', padding:'13px 14px 13px 40px', fontSize:15, color:'#0E1A14', background:'#F7FBF9', border:'1.5px solid #DBE7E1', borderRadius:12, outline:'none', transition:'border-color 0.15s, box-shadow 0.15s', fontFamily:'inherit', boxSizing:'border-box' },
  inputFocus: { borderColor:'#1A9E72', boxShadow:'0 0 0 3px rgba(26,158,114,0.12)', background:'#fff' },
  inputBlur: { borderColor:'#DBE7E1', boxShadow:'none', background:'#F7FBF9' },
  eye: { position:'absolute', right:12, fontSize:15, cursor:'pointer', opacity:0.7 },

  btn: { width:'100%', padding:'14px', marginTop:8, fontSize:15, fontWeight:700, color:'#fff', background:'linear-gradient(135deg,#0B5E45,#1A9E72)', border:'none', borderRadius:12, cursor:'pointer', boxShadow:'0 10px 24px rgba(26,158,114,0.34)', letterSpacing:'0.01em' },
  btnOff: { background:'linear-gradient(135deg,#A9CFBF,#BFE0D2)', boxShadow:'none', cursor:'not-allowed' },

  ssl: { display:'flex', alignItems:'center', gap:8, marginTop:18, padding:'10px 14px', background:'#EAF7F1', borderRadius:10, fontSize:11, color:'#0B5E45', fontWeight:600, border:'1px solid #CFEADF' },
  sslDot: { width:9, height:9, borderRadius:'50%', background:'#1A9E72', boxShadow:'0 0 0 3px rgba(26,158,114,0.18)', flexShrink:0 },

  demo: { background:'#fff', borderRadius:16, padding:'15px 16px 13px', border:'1px solid #E7F0EB', boxShadow:'0 6px 20px rgba(6,40,27,0.06)' },
  demoTitle: { fontSize:10.5, fontWeight:700, color:'#7A8A7E', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:9 },
  demoItem: { display:'flex', alignItems:'center', gap:10, padding:'9px 11px', borderRadius:10, cursor:'pointer', border:'1px solid #E6F1EB', background:'#F8FDFB', marginBottom:7, transition:'all 0.15s' },
  demoRole: { fontSize:11.5, fontWeight:800, color:'#0B5E45', width:54, flexShrink:0 },
  demoEmail: { fontSize:10.5, color:'#3A4A3E', fontFamily:'DM Mono,monospace', flex:1, wordBreak:'break-all' },
  demoPass: { fontSize:10.5, color:'#7A8A7E', fontFamily:'DM Mono,monospace', flexShrink:0 },
  demoHint: { fontSize:10, color:'#9AA89E', textAlign:'center', marginTop:4 },
});
